import { DestroyRef, Directive, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ZoudashUtils } from '@workspace/data-types';
import { ApplicationStore } from '@workspace/ng-data-access';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';

export interface DtoChangedDetection<TDto> {
  dto: TDto;
  changed: boolean;
}

@Directive()
export abstract class AbstractFormComponent<
  TDto extends object,
  TForm extends { [K in keyof TForm]: AbstractControl<unknown, unknown> } = Record<
    string,
    AbstractControl<unknown, unknown>
  >,
> {
  protected destroyRef = inject(DestroyRef);
  protected fb = inject(FormBuilder);
  protected appStore = inject(ApplicationStore);

  // Inputs
  dto = input<TDto | null | undefined>(undefined);
  requiredFields = input<string[]>([]);
  showOptionalFields = input<string[]>([]);
  allDisable = input(false);
  allReadonly = input(false);
  clearFormSignal = input<boolean>(false);

  // Outputs
  validityChanged = output<boolean>();
  dtoChanged = output<DtoChangedDetection<TDto>>();

  formGroup = signal<FormGroup<TForm>>(this.fb.group({}) as unknown as FormGroup<TForm>);

  formReady = signal(false);
  ogValue: TDto | undefined | null;

  debugClickCount = 0;
  debugClickTimer: ReturnType<typeof setTimeout> | undefined;

  private hasInitializedDto = false;

  constructor() {
    effect(() => {
      if (!this.hasInitializedDto) {
        const dtoValue = this.dto();
        if (dtoValue) {
          this.ogValue = dtoValue;
        }
        this.hasInitializedDto = true;
      }
      this.toggleDisable(this.allDisable());
    });

    effect(() => {
      if (this.allReadonly()) {
        this.formGroup().reset(this.ogValue ?? undefined);
      }
    });

    effect(() => {
      if (!this.formGroup()) return;

      this.formGroup()
        .statusChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((status) => this.validityChanged.emit(status === 'VALID'));

      this.formGroup()
        .valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(() => this.formReady() && this.formGroup().valid),
          map((value) => this.buildDtoFromForm(value as unknown as TDto)),
          distinctUntilChanged((a, b) => ZoudashUtils.isEqual(a, b)),
          tap((dto) => {
            if (dto === null) {
              console.error('Build dto from form is empty');
              return;
            }
            this.emitDto(dto);
          }),
        )
        .subscribe();
    });

    effect(() => {
      if (this.clearFormSignal()) {
        this.resetForm();
      }
    });
  }

  protected resetForm(): void {
    this.formGroup().reset();
    this.ogValue = this.dto();
  }

  protected toggleDisable(disabled: boolean) {
    if (disabled) this.formGroup().disable();
    else this.formGroup().enable();
  }

  protected isRequired(field: string) {
    return this.requiredFields().includes(field);
  }

  protected isVisible(field: string) {
    return this.showOptionalFields().includes(field);
  }

  protected emitDto(newDto: TDto): void {
    this.dtoChanged.emit({
      dto: newDto,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected debugForm(componentName: string) {
    if (this.appStore.debugMode()) {
      this.debugClickCount++;

      if (this.debugClickCount === 1) {
        this.debugClickTimer = setTimeout(() => {
          this.debugClickCount = 0;
        }, 500);
      }

      if (this.debugClickCount === 3) {
        clearTimeout(this.debugClickCount);
        console.log(`Debug formGroup ${componentName}:`, this.formGroup());
        this.debugClickCount = 0;
      }
    }
  }

  protected abstract buildDtoFromForm(value: unknown): TDto | null;
}
